import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Input, Space, Tag, Tooltip, Upload, message, Card, Row, Col, Modal, Form, InputNumber, Select } from 'antd';
import {
  SearchOutlined, DownloadOutlined, UploadOutlined, ReloadOutlined,
  ShoppingOutlined, InboxOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  EditOutlined, FilterOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import BASEURL from '../API/Config';
const { Option } = Select;


const StockUpdate = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockFilter, setStockFilter] = useState('all'); // 'all' or 'low'
  const [unitFilter, setUnitFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  useEffect(() => {
    setCurrentPage(1);
  }, [search, stockFilter, unitFilter]);



  const fetchStock = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/master/get-products-stock`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        const uniqueProducts = [];
        const seenKeys = new Set();
        for (const item of data) {
          if (item) {
            const key = `${item._id}-${item.variantUnit || 'Default'}-${item.measurementUnit || 'Main'}`;
            if (!seenKeys.has(key)) {
              seenKeys.add(key);
              uniqueProducts.push(item);
            }
          }
        }
        setProducts(uniqueProducts);
      } else {
        setProducts([]);
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to load stock data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStock();
  }, []);

  const handleDownloadTemplate = () => {
    window.open(`${BASEURL}/master/download-stock-template`, '_blank');
  };

  const handleDownloadFilteredTemplate = () => {
    if (filtered.length === 0) {
      message.warning("No data found to export.");
      return;
    }

    const exportData = filtered.map(item => ({
      'Title': item.title,
      'Unit': item.variantUnit || 'Default',
      'Measurement': item.measurementUnit || 'Main',
      'Current': item.currentStock,
      'Update Qty': 0,
      'Product ID': item._id
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Move Product ID to the end (Column F) and hide it
    worksheet['!cols'] = [
      { wch: 50 },      // A: Title
      { wch: 15 },      // B: Unit
      { wch: 15 },      // C: Measurement
      { wch: 12 },      // D: Current
      { wch: 15 },      // E: Update Qty
      { hidden: true }  // F: Product ID
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Update Filtered');
    XLSX.writeFile(workbook, `stock_update_template_${stockFilter}_${unitFilter}.xlsx`);


  };


  const uploadProps = {
    name: 'file',
    action: `${BASEURL}/master/upload-stock-excel`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    showUploadList: false,
    onChange(info) {
      console.log("Upload Status:", info.file.status, info.file.response);
      if (info.file.status === 'uploading') {
        setUploading(true);
        return;
      }
      if (info.file.status === 'done') {
        setUploading(false);
        if (!info.file.response) {
          message.error("Server responded with empty data.");
          return;
        }
        const { message: msg, updatedCount, errors } = info.file.response;
        if (errors && errors.length > 0) {
          Swal.fire({
            title: 'Upload Partially Successful',
            html: `<p>${msg}</p><div style="text-align: left; max-height: 200px; overflow-y: auto;"><small>${errors.join('<br/>')}</small></div>`,
            icon: 'warning'
          });
        } else {
          Swal.fire('Success', msg || `Successfully updated ${updatedCount} products.`, 'success');
        }
        fetchStock();
      } else if (info.file.status === 'error') {
        setUploading(false);
        const errMsg = info.file.response?.message || "File upload failed. Please check the console.";
        message.error(errMsg);
        console.error("Upload Error Details:", info.file);
      }
    },

  };

  const filtered = products.filter(p => {
    const titleMatch = p.title ? p.title.toLowerCase().includes(search.toLowerCase()) : false;
    const idMatch = p._id ? p._id.toLowerCase().includes(search.toLowerCase()) : false;
    const matchesSearch = titleMatch || idMatch;
    const matchesStock = stockFilter === 'all' || p.currentStock <= 10;
    const matchesUnit = unitFilter === 'all' || p.variantUnit === unitFilter;
    return matchesSearch && matchesStock && matchesUnit;
  });

  const uniqueUnits = ['all', ...new Set(products.map(p => p.variantUnit).filter(Boolean))];


  const columns = [
    {
      title: "S.No",
      key: "idx",
      width: 70,
      render: (_, record) => {
        const index = filtered.indexOf(record);
        return <span style={{ color: "#6b7280" }}>{index !== -1 ? index + 1 : ""}</span>;
      }
    },
    {
      title: 'PRODUCT NAME',
      dataIndex: 'title',
      key: 'title',
      render: (title) => (
        <div style={{ fontWeight: 600, color: '#212121' }}>{title}</div>
      )
    },
    {
      title: 'UNIT',
      dataIndex: 'variantUnit',
      key: 'unit',
      width: 100,
      render: (unit) => <Tag color="orange">{unit || 'Default'}</Tag>
    },
    {
      title: 'MEASUREMENT',
      dataIndex: 'measurementUnit',
      key: 'measurement',
      width: 130,
      render: (measurement) => <Tag color="blue">{measurement || 'Main'}</Tag>
    },
    {
      title: 'CURRENT STOCK',
      dataIndex: 'currentStock',
      key: 'stock',
      width: 150,
      align: 'center',
      render: (stock) => (
        <Tag color={stock <= 10 ? 'red' : 'green'} style={{ minWidth: 60, textAlign: 'center', borderRadius: 4 }}>
          {stock}
        </Tag>
      )
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Update Stock">
          <Button 
            type="text" 
            icon={<EditOutlined style={{ color: '#2874f0' }} />} 
            onClick={() => handleEditClick(record)}
          />
        </Tooltip>
      )
    }
  ];

  const handleEditClick = (record) => {
    setEditingProduct(record);
    form.setFieldsValue({ 
      currentStock: record.currentStock,
      newStock: 0 
    });
    setIsModalVisible(true);
  };



  const handleUpdateStock = async (values) => {
    try {
      const res = await fetch(`${BASEURL}/master/update-single-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          productId: editingProduct._id,
          variantUnit: editingProduct.variantUnit,       // title (fallback)
          variantUnitId: editingProduct.variantUnitId,  // ObjectId (preferred)
          measurementUnit: editingProduct.measurementUnit,
          newStock: values.newStock,
        }),
      });

      const data = await res.json();
      if (data.success) {
        message.success(data.message);
        
        // Optimistically update the local state for instant feedback
        setProducts(prev => prev.map(p => {
          if (p._id === editingProduct._id && p.variantUnit === editingProduct.variantUnit && p.measurementUnit === editingProduct.measurementUnit) {
            const updatedStock = p.currentStock + values.newStock;
            return { ...p, currentStock: updatedStock < 0 ? 0 : updatedStock };
          }
          return p;
        }));

        setIsModalVisible(false);
        fetchStock(); // Still refresh to ensure sync with server
      } else {
        message.error(data.message || 'Update failed');
      }

    } catch (error) {
      message.error('An error occurred while updating stock');
    }
  };


  return (
    <div style={{ padding: '0 10px' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Inventory Management</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 24, fontWeight: 700, color: '#212121' }}>Stock Update</h2>
        </div>
        <Space size="middle">
          <Button icon={<ReloadOutlined />} onClick={fetchStock} loading={loading}>Refresh</Button>
          <Tooltip title={stockFilter !== 'all' || unitFilter !== 'all' ? "Download filtered template" : "Download full template"}>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={stockFilter !== 'all' || unitFilter !== 'all' ? handleDownloadFilteredTemplate : handleDownloadTemplate} 
              style={{ color: '#059669', borderColor: '#059669' }}
            >
              {stockFilter !== 'all' || unitFilter !== 'all' ? "Download Filtered" : "Download Template"}
            </Button>
          </Tooltip>
          <Upload {...uploadProps}>
            <Button type="primary" icon={<UploadOutlined />} loading={uploading} style={{ background: '#2874f0', borderColor: '#2874f0' }}>
              Upload Excel
            </Button>
          </Upload>
        </Space>

      </div>

      {/* Info Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ borderRadius: 8, borderLeft: '4px solid #2874f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: '#e8f1ff', padding: 8, borderRadius: 6, color: '#2874f0' }}>
                <ShoppingOutlined style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Total Products</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{products.length}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ borderRadius: 8, borderLeft: '4px solid #ef4444' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: '#fee2e2', padding: 8, borderRadius: 6, color: '#ef4444' }}>
                <ExclamationCircleOutlined style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Low Stock (≤10)</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{products.filter(p => p.currentStock <= 10).length}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ borderRadius: 8, borderLeft: '4px solid #10b981' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: '#d1fae5', padding: 8, borderRadius: 6, color: '#10b981' }}>
                <CheckCircleOutlined style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Available Products</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{products.filter(p => p.currentStock > 10).length}</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table Section */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Input 
            allowClear
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} 
            placeholder="Search products..."
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            style={{ width: 250, borderRadius: 6 }} 
          />
          
          <Select 
            value={stockFilter} 
            onChange={setStockFilter} 
            style={{ width: 160 }}
            prefix={<FilterOutlined />}
          >
            <Option value="all">All Stock Status</Option>
            <Option value="low">Low Stock Only</Option>
          </Select>

          <Select 
            value={unitFilter} 
            onChange={setUnitFilter} 
            style={{ width: 160 }}
            placeholder="Filter by Unit"
          >
            {uniqueUnits.map(unit => (
              <Option key={unit} value={unit}>{unit === 'all' ? 'All Units' : unit}</Option>
            ))}
          </Select>

          <div style={{ marginLeft: 'auto', fontSize: 14, color: '#6b7280' }}>
            Showing <strong>{filtered.length}</strong> variants
          </div>
        </div>

        <Table 
          columns={columns} 
          dataSource={filtered} 
          rowKey={(record) => `${record._id}-${record.variantUnit || 'Default'}-${record.measurementUnit || 'Main'}`} 
          loading={loading}
          pagination={{ 
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"], 
            showTotal: (total) => `${total} products`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }
          }} 
          size="middle" 
          style={{ padding: '0 8px' }}
        />
      </div>

      {/* Instructions Card */}
      <Card title="How to update stock in bulk" size="small" style={{ marginTop: 24, borderRadius: 8, background: '#f9fafb' }}>
        <ol style={{ fontSize: 13, color: '#4b5563', paddingLeft: 20, margin: 0 }}>
          <li>Download the <strong>Stock Template</strong> using the button above.</li>
          <li>Open the Excel file and update the <strong>Update Qty</strong> column for the products you want to change. Use positive numbers to add stock, and negative numbers (e.g., -5) to deduct stock.</li>
          <li>Do not modify the <strong>Product ID</strong>, <strong>Product Title</strong>, or <strong>Unit</strong> columns as they are used for identification.</li>
          <li>Save the Excel file and upload it using the <strong>Upload Excel</strong> button.</li>
          <li>The system will process the file and update the stock levels automatically.</li>
        </ol>
      </Card>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <EditOutlined style={{ color: '#2874f0' }} />
            <span>Update Single Variant Stock</span>
          </div>
        }
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        {editingProduct && (
          <Form form={form} layout="vertical" onFinish={handleUpdateStock} initialValues={{ newStock: editingProduct.currentStock }}>
            <div style={{ marginBottom: 20, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: '#212121', marginBottom: 4 }}>{editingProduct.title}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Unit: <Tag color="blue">{editingProduct.variantUnit || 'Default'}</Tag> | 
                Measurement: <span style={{ fontWeight: 600 }}>{editingProduct.measurementUnit || 'Main'}</span>
              </div>
            </div>
            
            <Form.Item 
              label="Current Stock" 
              name="currentStock"
              initialValue={editingProduct.currentStock}
            >
              <InputNumber disabled style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item 
              label="Quantity to Add/Deduct" 
              name="newStock" 
              rules={[{ required: true, message: 'Please input quantity to add or deduct (use negative values to deduct)' }]}
            >
              <InputNumber style={{ width: '100%' }} placeholder="Enter quantity (e.g., 5 to add, -5 to deduct)..." autoFocus />
            </Form.Item>


            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" style={{ background: '#2874f0', borderColor: '#2874f0' }}>
                Update Stock
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>

  );
};

export default StockUpdate;
